import { create } from 'zustand';
import { isModuleOn, type ModuleKey } from '@matix/types';
import { tenantConfigAPI } from '@matix/api-client';
import { EMPTY_BARCODE_SCANNER_CONFIG, type BarcodeScannerConfig } from '@matix/utils';
import { useCart } from './cart';

/**
 * Tenant POS feature-flag store.
 *
 * Mirrors the legacy `mstr_ConfigurationParameter` flags (admin → Settings →
 * POS Settings). Loaded once on cashier boot via `loadTenantConfig()`:
 *
 *   isRestaurant       — false hides the dine-in / tables flow (the Dine-in
 *                        order-mode pill and the cart's table chip).
 *   showProductImages  — gates whether catalog tiles show per-item photos.
 *   isTakeAway / isDelivery / isDineIn — gate the matching order-mode pills.
 *   isPickUp / isMembership            — order types (no cashier flow yet).
 *   isTotalPriceChanges + whenChangeTotal — allow editing a line's total price
 *                        and whether that adjusts the quantity or the price.
 *
 * Defaults (and the fallback when the fetch fails) keep the historical
 * behaviour: restaurant mode on, images on, the three existing order modes on —
 * so a misconfigured/offline tenant behaves exactly as before.
 */

export type WhenChangeTotal = 'quantity' | 'price';

interface TenantConfigState {
  isRestaurant: boolean;
  showProductImages: boolean;
  isTakeAway: boolean;
  isDelivery: boolean;
  isPickUp: boolean;
  isDineIn: boolean;
  isMembership: boolean;
  isTotalPriceChanges: boolean;
  whenChangeTotal: WhenChangeTotal;
  /** false → hide the per-line salesman selects entirely. */
  isSalesmanImplemented: boolean;
  /** true → a salesman is required on every non-membership cart line. */
  isSalesmanMandatory: boolean;
  /** true → offer a second salesman per line. */
  activateSecondSalesman: boolean;
  /** true → print a per-salesman summary after the customer receipt. */
  printSalesmanSummary: boolean;
  /** true → match the whole name/code/barcode, not a substring. */
  fullBarcodeScan: boolean;
  /** true → a repeat scan bumps the existing cart line. */
  combineQtyOnScan: boolean;
  /**
   * Embedded-barcode layout — a weighing shop's own label carries the item
   * code AND the quantity, and this says where each sits. Inert unless
   * `enabled` and complete; see `parseEmbeddedBarcode` in @matix/utils.
   */
  barcodeScanner: BarcodeScannerConfig;
  /**
   * false → the till may not take a sale with no network.
   *
   * Defaults FALSE, which matters: the default is what applies when the config
   * fetch itself fails, and a failed fetch usually means the network is down —
   * exactly when the offline path would otherwise engage. Defaulting true would
   * let a tenant who never enabled the feature start queueing sales the moment
   * their connection wobbled.
   */
  offlineSalesEnabled: boolean;
  /** false → hide parking (hold) an order. */
  isHoldImplemented: boolean;
  /** true → print the shift serial on the Z-report. */
  printShiftSerial: boolean;
  /** true → an order cannot be charged without a customer attached. */
  isCustomerMandatory: boolean;
  /**
   * POS_CreditRequiresCreditCustomer — the Credit tender (Code 2) only alone
   * and for a credit customer. The server enforces it; the till says why first.
   */
  creditRequiresCreditCustomer: boolean;
  /** true → a customer cannot be saved without a category. */
  customerCategoryMandatory: boolean;
  /** true → show the customer↔branch linkage UI in the customer popup. */
  customerBranchLink: boolean;
  /** true → the customer card / form offer the customer's measurements (POS_CustomerMeasurements). */
  customerMeasurements: boolean;
  /** true → the Appointments module is enabled — show the link to the scheduling app. */
  appointmentsEnabled: boolean;
  /**
   * false → the tenant does not allow selling below stock: the cart shows the
   * on-hand quantity on every line, warns as soon as a line exceeds it, and
   * the server refuses the sale. Defaults TRUE — the ERP's own default, and
   * the behaviour every tenant had before this flag was read.
   */
  allowNegativeStock: boolean;
  /**
   * How the "back to stock" box starts on a return line of a
   * RestockOnReturn item. Defaults TRUE — the legacy POS always starts it
   * ticked; a tenant that wants returns written off unless the cashier says
   * otherwise turns it off in POS Settings.
   */
  restockOnReturnDefault: boolean;
  /**
   * Business-profile modules from the setup wizard. null = every module —
   * every tenant onboarded before the wizard, and the fallback on a failed
   * fetch, so this can only ever narrow what a wizard-configured tenant sees.
   */
  enabledModules: string[] | null;
  loaded: boolean;
  set: (cfg: Omit<TenantConfigState, 'loaded' | 'set'>) => void;
}

export const useTenantConfig = create<TenantConfigState>((set) => ({
  isRestaurant: true,
  showProductImages: true,
  isTakeAway: true,
  isDelivery: true,
  isPickUp: false,
  isDineIn: true,
  isMembership: false,
  isTotalPriceChanges: false,
  whenChangeTotal: 'price',
  isSalesmanImplemented: true,
  isSalesmanMandatory: false,
  activateSecondSalesman: false,
  printSalesmanSummary: false,
  fullBarcodeScan: false,
  combineQtyOnScan: false,
  barcodeScanner: EMPTY_BARCODE_SCANNER_CONFIG,
  offlineSalesEnabled: false,
  isHoldImplemented: true,
  printShiftSerial: false,
  isCustomerMandatory: false,
  creditRequiresCreditCustomer: false,
  customerCategoryMandatory: false,
  customerBranchLink: false,
  customerMeasurements: false,
  appointmentsEnabled: false,
  allowNegativeStock: true,
  restockOnReturnDefault: true,
  enabledModules: null,
  loaded: false,
  set: (cfg) => set({ ...cfg, loaded: true }),
}));

/**
 * Fetch + cache the tenant's POS flags. Errors are swallowed so a missing
 * setting never blocks the till. When the tenant is not a restaurant and the
 * active cart somehow opened on dine-in (e.g. a stale default order type), the
 * mode is coerced back to takeaway so the cashier never lands on a hidden flow.
 */
export async function loadTenantConfig(): Promise<void> {
  try {
    const cfg = await tenantConfigAPI.get();
    useTenantConfig.getState().set({
      isRestaurant: cfg.is_restaurant,
      showProductImages: cfg.is_show_product_image,
      isTakeAway: cfg.is_takeaway,
      isDelivery: cfg.is_delivery,
      isPickUp: cfg.is_pickup,
      isDineIn: cfg.is_dine_in,
      isMembership: cfg.is_membership,
      isTotalPriceChanges: cfg.is_total_price_changes,
      whenChangeTotal: cfg.when_change_total,
      isSalesmanImplemented: cfg.is_salesman_implemented,
      isSalesmanMandatory: cfg.is_salesman_mandatory,
      activateSecondSalesman: cfg.activate_second_salesman,
      printSalesmanSummary: cfg.print_salesman_summary,
      fullBarcodeScan: cfg.full_barcode_scan,
      combineQtyOnScan: cfg.combine_qty_on_scan,
      // A missing block means an API older than this build — the feature is
      // off, which is the same as unconfigured.
      barcodeScanner: cfg.barcode_scanner
        ? {
            enabled: cfg.barcode_scanner.enabled,
            totalDigits: cfg.barcode_scanner.total_digits,
            itemCodeDigits: cfg.barcode_scanner.item_code_digits,
            itemCodeStart: cfg.barcode_scanner.item_code_start,
            qtyDigits: cfg.barcode_scanner.qty_digits,
            qtyStart: cfg.barcode_scanner.qty_start,
            qtyWholeDigits: cfg.barcode_scanner.qty_whole_digits,
          }
        : EMPTY_BARCODE_SCANNER_CONFIG,
      offlineSalesEnabled: cfg.offline_sales_enabled ?? false,
      isHoldImplemented: cfg.is_hold_implemented,
      printShiftSerial: cfg.print_shift_serial,
      isCustomerMandatory: cfg.is_customer_mandatory,
      creditRequiresCreditCustomer: cfg.credit_requires_credit_customer ?? false,
      customerCategoryMandatory: cfg.customer_category_is_mandatory,
      customerBranchLink: cfg.customer_branch_link,
      customerMeasurements: cfg.customer_measurements ?? false,
      appointmentsEnabled: cfg.appointments_enabled,
      allowNegativeStock: cfg.allow_negative_stock ?? true,
      restockOnReturnDefault: cfg.restock_on_return_default ?? true,
      enabledModules: cfg.enabled_modules ?? null,
    });

    // If the active cart sits on a mode that's now hidden, coerce it to the
    // first visible one so the cashier never lands on a hidden flow.
    const dineInVisible = cfg.is_restaurant && cfg.is_dine_in;
    const visible: Record<string, boolean> = {
      takeaway: cfg.is_takeaway,
      delivery: cfg.is_delivery,
      'dine-in': dineInVisible,
      pickup: cfg.is_pickup,
      membership: cfg.is_membership,
    };
    const cart = useCart.getState();
    if (!visible[cart.orderMode]) {
      const fallback = (['takeaway', 'delivery', 'dine-in', 'pickup', 'membership'] as const).find(
        (m) => visible[m],
      );
      if (fallback) cart.setOrderMode(fallback);
    }
  } catch (err) {
    // Mark the store loaded anyway: the defaults above ARE the fallback, and
    // consumers that wait for `loaded` (the order-mode pills) must not stay
    // blank forever because a flag fetch failed.
    useTenantConfig.setState({ loaded: true });
    // eslint-disable-next-line no-console
    console.warn('[tenant-config] failed to load, using defaults', err);
  }
}

/** Is this business-profile module on for the signed-in tenant? */
export function useModuleOn(module: ModuleKey): boolean {
  return useTenantConfig((s) => isModuleOn(s.enabledModules, module));
}
